import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authApi } from "../services/authApi";
import { useAuthStore } from "../stores/authStore";
import { useAnalytics, EVENTS } from "./useAnalytics";

/**
 * Central auth hook — provides login flow, current user, logout.
 */
export function useAuth() {
  const queryClient = useQueryClient();
  const { track } = useAnalytics();
  const {
    user,
    isAuthenticated,
    role,
    accessToken,
    refreshToken,
    login,
    logout: storeLogout,
    setUser,
    setAccessToken,
  } = useAuthStore();

  // Fetch current user on mount (if we have a refresh token but no user)
  const { isLoading: isLoadingUser } = useQuery({
    queryKey: ["auth", "me"],
    queryFn: async () => {
      // If we only have refreshToken (e.g. after page refresh), get a new
      // accessToken first.  The response interceptor in api.js auto-unwraps
      // the envelope, so res.data is already the inner payload object.
      if (!useAuthStore.getState().accessToken && refreshToken) {
        const res = await authApi.refresh(refreshToken);
        // res.data may already be unwrapped by the axios interceptor
        const tokens = res.data && typeof res.data === "object" ? res.data : {};
        const newAccessToken = tokens.accessToken;
        const newRefreshToken = tokens.refreshToken;
        if (!newAccessToken) {
          // Refresh returned an unexpected shape — log out cleanly
          useAuthStore.getState().logout();
          throw new Error("Token refresh returned no access token");
        }
        setAccessToken(newAccessToken);
        if (newRefreshToken) {
          useAuthStore.getState().setRefreshToken(newRefreshToken);
        }
      }
      const { data } = await authApi.getMe();
      setUser(data);
      return data;
    },
    enabled: !!accessToken || !!refreshToken,
    retry: false,
    staleTime: 1000 * 60 * 5,
  });

  const sendOtpMutation = useMutation({
    mutationFn: ({ mobile }) => authApi.sendOtp(mobile),
  });

  const verifyOtpMutation = useMutation({
    mutationFn: ({ mobile, otp, name }) =>
      authApi.verifyOtp({ mobile, otp, name }),
    onSuccess: (response) => {
      const { user: userData, tokens } = response.data;
      const { accessToken: at, refreshToken: rt } = tokens;
      queryClient.clear();
      login(userData, at, rt);
      track(userData.isNewUser ? EVENTS.SIGNUP : EVENTS.LOGIN_SUCCESS, {
        userId: userData._id,
      });
    },
  });

  // Logout
  const logoutMutation = useMutation({
    mutationFn: () => authApi.logout(),
    onSettled: () => {
      storeLogout();
      queryClient.clear();
    },
  });

  return {
    user,
    isAuthenticated,
    role,
    isLoadingUser,

    sendOtp: sendOtpMutation,
    sendOtpLoading: sendOtpMutation.isPending,
    sendOtpError: sendOtpMutation.error,

    verifyOtp: verifyOtpMutation,
    verifyOtpLoading: verifyOtpMutation.isPending,
    verifyOtpError: verifyOtpMutation.error,

    logout: logoutMutation.mutate,
    logoutLoading: logoutMutation.isPending,
  };
}
